import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import {
  ClearButton,
  SelectDropDown,
  DatePicker
} from 'components/common/Elements/Elements';

import { createUnderlineToSpace } from '../../helpers/helpers';
import { createFilterCountry, createFilterSize } from 'utils/common';

import classes from './FiltersBlock.module.scss';

const FiltersBlock = ({
  filter,
  setFilter,
  startFilter,
  setStartFilter,
  filterButton,
  filters,
  trigerResetDatePicker,
  resetAllfilters,
  selectLoader,
  setVisibleFilterSave,
  setVisibleFilterEdit,
  changeFilter
}) => {
  const { t } = useTranslation(['activity']);

  // Pending filter state - tracks uncommitted filter changes
  const [pendingFilters, setPendingFilters] = React.useState({});

  // Configuration: filters that defer application until dropdown closes
  //
  // Rationale: These filters coordinate with auto-show column behavior.
  // Deferring prevents:
  // 1. Multiple rapid API calls as user browses options
  // 2. Race conditions between filter updates and column visibility changes
  // 3. Flickering UI as columns appear/disappear during option selection
  //
  // To add new deferred filter: Add key with `true` value, then implement
  // onMenuClose handler in corresponding SelectDropDown component below
  const DEFERRED_FILTERS = {
    terminationStatus: true
    // Easy to extend: healthStatus: true, status: true, etc.
  };

  const numberОfFilters = 24;
  let companyPoc = [];
  let companySize = [];
  let companyCountry = [];
  let companyPaymentType = [];

  const handleFilter = e => {
    const filterName = e.target;
    const filterValue = e.value;

    if (DEFERRED_FILTERS[filterName]) {
      // DEFERRED: Store pending, update UI, don't trigger API yet
      setPendingFilters({ ...pendingFilters, [filterName]: filterValue });
      setFilter({ ...filter, [filterName]: filterValue, currentPage: 1 });
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
      // DON'T call setStartFilter here - wait for dropdown close
    } else {
      // IMMEDIATE: Existing behavior for all other filters
      setFilter({ ...filter, [filterName]: filterValue, currentPage: 1 });
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
  };

  const handleMenuClose = filterName => {
    // Apply pending changes on dropdown close
    if (
      DEFERRED_FILTERS[filterName] &&
      Object.prototype.hasOwnProperty.call(pendingFilters, filterName)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [filterName]: _, ...remainingPending } = pendingFilters;
      setPendingFilters(remainingPending);
      setStartFilter(!startFilter); // NOW trigger API
    }
  };

  function checkAndSetFilter(key, value) {
    if (!isEqual(filter[key], value)) {
      setFilter({ ...filter, [key]: value, currentPage: 1 });
      // in cross filters after changing the date you need to get new filters
      setStartFilter(!startFilter);
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
  }

  const handleDateChange = value => {
    const key = 'firstPaymentDate';
    checkAndSetFilter(key, value);
  };
  const handleBillDate = value => {
    const key = 'nextBillingDate';
    checkAndSetFilter(key, value);
  };
  const handleInvDate = value => {
    const key = 'firstInvoiceAt';
    checkAndSetFilter(key, value);
  };
  const handleQbrDate = value => {
    const key = 'qbr.occurredDate';
    checkAndSetFilter(key, value);
  };

  (function () {
    createFilterSize(filters.companySize, companySize);
    createFilterCountry(filters.country, companyCountry);
    createUnderlineToSpace(filters.poc, companyPoc);
    createUnderlineToSpace(filters.paymentType, companyPaymentType);
  })();

  return (
    <div className={cn(filterButton ? classes.filters : classes.filters_close)}>
      <SelectDropDown
        options={filters.status}
        input={{ value: filter.status, onChange: handleFilter }}
        name={'status'}
        label={t('activity:filters.CUSTOMER_STATUS')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={companySize}
        input={{ value: filter.companySize, onChange: handleFilter }}
        name={'companySize'}
        label={t('activity:filters.COMPANY_SIZE')}
        multi
        renderKey={'label'}
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.industry}
        input={{ value: filter.industry, onChange: handleFilter }}
        name={'industry'}
        label={t('activity:filters.INDUSTRY')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.plan}
        input={{ value: filter.plan, onChange: handleFilter }}
        name={'plan'}
        label={t('activity:filters.PRICING_PLAN')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.billingCycle}
        input={{
          value: filter.billingCycle,
          onChange: handleFilter
        }}
        name={'billingCycle'}
        label={t('activity:filters.BILLING_CYCLE')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <DatePicker
        label={t('activity:filters.NEXT_BILLING')}
        input={{
          value: filter.nextBillingDate,
          onChange: handleBillDate
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <DatePicker
        label={t('activity:filters.FIRST_PAYMENT')}
        input={{
          value: filter.firstPaymentDate,
          onChange: handleDateChange
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <DatePicker
        label={t('activity:filters.FIRST_INVOICE')}
        input={{
          value: filter.firstInvoiceAt,
          onChange: handleInvDate
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <SelectDropDown
        options={companyPaymentType}
        input={{ value: filter.paymentType, onChange: handleFilter }}
        name={'paymentType'}
        label={t('activity:filters.PAYMENT_METHOD')}
        renderKey={'label'}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.region}
        input={{ value: filter.region, onChange: handleFilter }}
        name={'region'}
        label={t('activity:filters.REGION')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={companyCountry}
        input={{ value: filter.country, onChange: handleFilter }}
        name={'country'}
        label={t('activity:filters.COUNTRY')}
        searchKeys={['value', 'findName']}
        renderKey={'fullName'}
        checkRenderKey={'fullName'}
        multi
        searchable
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.customerSuccessEngineer}
        input={{
          value: filter.customerSuccessEngineer,
          onChange: handleFilter
        }}
        name={'customerSuccessEngineer'}
        label={t('activity:about.CS_ENGINEER')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.accountManager}
        input={{ value: filter.accountManager, onChange: handleFilter }}
        name={'accountManager'}
        label={t('activity:filters.ACCOUNT_MANAGER_')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.partnerType}
        input={{ value: filter.partnerType, onChange: handleFilter }}
        name={'partnerType'}
        label={t('activity:filters.PARTNER_TYPE')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.healthStatus}
        input={{ value: filter.healthStatus, onChange: handleFilter }}
        name={'healthStatus'}
        label={t('activity:filters.HEALTH_STATUS')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters.terminationStatus}
        input={{
          value: filter.terminationStatus,
          onChange: handleFilter
        }}
        name={'terminationStatus'}
        label={t('activity:filters.CANCELLED_STATUS')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
        onMenuClose={() => handleMenuClose('terminationStatus')}
      />
      <DatePicker
        label={t('activity:filters.LAST_QBR_DATE')}
        input={{
          value: filter['qbr.occurredDate'],
          onChange: handleQbrDate
        }}
        trigerResetDatePicker={trigerResetDatePicker}
      />
      <SelectDropDown
        options={filters['qbr.sentiment']}
        input={{ value: filter['qbr.sentiment'], onChange: handleFilter }}
        name={'qbr.sentiment'}
        label={t('activity:filters.SENTIMENT')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={
          filters['qbr.isExist']
            ? filters['qbr.isExist'].map(item =>
                item === true ? 'True' : 'False'
              )
            : []
        }
        input={{ value: filter['qbr.isExist'], onChange: handleFilter }}
        name={'qbr.isExist'}
        label={t('activity:filters.IS_QBR')}
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters['coupons.name']}
        input={{ value: filter['coupons.name'], onChange: handleFilter }}
        name={'coupons.name'}
        label={t('activity:filters.COUPONS_NAME')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters['coupons.type']}
        input={{ value: filter['coupons.type'], onChange: handleFilter }}
        name={'coupons.type'}
        label={t('activity:filters.COUPONS_TYPE')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />
      <SelectDropDown
        options={filters['coupons.duration']}
        input={{ value: filter['coupons.duration'], onChange: handleFilter }}
        name={'coupons.duration'}
        label={t('activity:filters.COUPONS_DURATION')}
        multi
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />

      <SelectDropDown
        options={companyPoc}
        input={{ value: filter.poc, onChange: handleFilter }}
        name={'poc'}
        label={t('activity:filters.POC_STATUS')}
        multi
        renderKey="label"
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
      />

      <SelectDropDown
        options={
          filters['isTestTenant']
            ? filters['isTestTenant'].map(item =>
                item === true ? 'True' : 'False'
              )
            : []
        }
        input={{ value: filter.isTestTenant, onChange: handleFilter }}
        name={'isTestTenant'}
        label={t('activity:filters.IS_TEST_TENANT')}
        closeTrigger={{
          trigger: startFilter,
          setTrigger: setStartFilter
        }}
        reload
        selectLoader={selectLoader}
        defaultInputValue={'false'}
      />
      <ClearButton
        onClick={() => resetAllfilters(false, true, false, true)}
        numberОfFilters={numberОfFilters}
      />
    </div>
  );
};

FiltersBlock.propTypes = {
  filter: PropTypes.object,
  setFilter: PropTypes.func,
  startFilter: PropTypes.bool,
  setStartFilter: PropTypes.func,
  filterButton: PropTypes.bool,
  filters: PropTypes.object,
  trigerResetDatePicker: PropTypes.bool,
  resetAllfilters: PropTypes.func,
  selectLoader: PropTypes.bool,
  setVisibleFilterSave: PropTypes.func,
  setVisibleFilterEdit: PropTypes.func,
  changeFilter: PropTypes.string
};

export default FiltersBlock;
